import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import WaveformV2 from '@/components/WaveformV2';
import VUMeter from '@/components/VUMeter';
import { AudioEffectsManager } from '@/utils/AudioEffects';
import { toast } from 'sonner';
import { 
  Play, Pause, Sliders, Activity, Radio, Zap, Music2, Maximize2
} from 'lucide-react';

export default function ProducerView({
  leftDeck,
  rightDeck,
  leftAudioRef,
  rightAudioRef,
  tracks,
  selectedLibraryTrack,
  setSelectedLibraryTrack,
  loadTrackToDeck,
  togglePlay,
  updateVolume,
  updatePitch,
  updateEQ,
  setCuePoint,
  jumpToCue,
  clearCuePoints,
  setLoopPoint,
  toggleLoop,
  crossfader,
  updateCrossfader,
  masterVolume,
  updateMasterVolume,
  syncBPM,
  focusedDeck,
  setFocusedDeck,
  isFullscreen,
  isMobile,
  isTablet
}) {
  // Producer mode specific state
  const [showEffects, setShowEffects] = useState(true);
  const [showSpectrum, setShowSpectrum] = useState(true);
  const [showMIDI, setShowMIDI] = useState(false);
  const [performanceMode, setPerformanceMode] = useState(false);
  
  // Audio effects state
  const [leftEffects, setLeftEffects] = useState({
    echo: 0,
    reverb: 0,
    filter: 50
  });
  
  const [rightEffects, setRightEffects] = useState({
    echo: 0,
    reverb: 0,
    filter: 50
  });

  // Audio effects managers
  const leftEffectsManagerRef = useRef(null);
  const rightEffectsManagerRef = useRef(null);

  // MIDI Controller state
  const [midiDevices, setMidiDevices] = useState([]);
  const [selectedMIDI, setSelectedMIDI] = useState(null);
  const [midiMapping, setMidiMapping] = useState({});
  const [learningMode, setLearningMode] = useState(null);

  // Initialize Audio Effects Managers
  useEffect(() => {
    if (leftAudioRef.current && !leftEffectsManagerRef.current) {
      leftEffectsManagerRef.current = new AudioEffectsManager(leftAudioRef.current);
    }
    if (rightAudioRef.current && !rightEffectsManagerRef.current) {
      rightEffectsManagerRef.current = new AudioEffectsManager(rightAudioRef.current);
    }

    // Initialize on first play
    const initLeft = () => {
      if (leftEffectsManagerRef.current) {
        leftEffectsManagerRef.current.initialize();
      }
    };
    const initRight = () => {
      if (rightEffectsManagerRef.current) {
        rightEffectsManagerRef.current.initialize();
      }
    };

    leftAudioRef.current?.addEventListener('play', initLeft, { once: true });
    rightAudioRef.current?.addEventListener('play', initRight, { once: true });

    return () => {
      leftAudioRef.current?.removeEventListener('play', initLeft);
      rightAudioRef.current?.removeEventListener('play', initRight);
      leftEffectsManagerRef.current?.dispose();
      rightEffectsManagerRef.current?.dispose();
    };
  }, [leftAudioRef.current, rightAudioRef.current]);

  // Apply effects when changed
  useEffect(() => {
    if (leftEffectsManagerRef.current?.effectsEnabled) {
      leftEffectsManagerRef.current.setFilter(leftEffects.filter);
      leftEffectsManagerRef.current.setEcho(leftEffects.echo);
      leftEffectsManagerRef.current.setReverb(leftEffects.reverb);
    }
  }, [leftEffects]);

  useEffect(() => {
    if (rightEffectsManagerRef.current?.effectsEnabled) {
      rightEffectsManagerRef.current.setFilter(rightEffects.filter);
      rightEffectsManagerRef.current.setEcho(rightEffects.echo);
      rightEffectsManagerRef.current.setReverb(rightEffects.reverb);
    }
  }, [rightEffects]);

  // Initialize MIDI
  useEffect(() => {
    if (navigator.requestMIDIAccess) {
      navigator.requestMIDIAccess().then((midiAccess) => {
        const inputs = Array.from(midiAccess.inputs.values());
        setMidiDevices(inputs);
        
        if (inputs.length > 0) {
          console.log('MIDI devices found:', inputs.map(d => d.name));
          toast.success(`Found ${inputs.length} MIDI device(s)`);
          
          // Auto-select first device
          if (!selectedMIDI && inputs[0]) {
            setSelectedMIDI(inputs[0].id);
            setupMIDIListener(inputs[0]);
          }
        } else {
          console.log('No MIDI devices connected');
        }
      }).catch(err => {
        console.warn('MIDI access denied:', err);
        toast.error('MIDI access denied. Check browser permissions.');
      });
    } else {
      console.warn('Web MIDI API not supported in this browser');
    }
  }, []);

  // Setup MIDI listener
  const setupMIDIListener = (device) => {
    device.onmidimessage = (message) => {
      const [status, control, value] = message.data;
      
      // Control Change messages (status 176 for channel 1)
      if (status === 176) {
        handleMIDIControl(control, value);
      }
    };
    toast.info(`MIDI: ${device.name} connected`);
  };

  // Handle MIDI control messages
  const handleMIDIControl = (control, value) => {
    if (learningMode) {
      // Learning mode: map this control
      setMidiMapping(prev => ({
        ...prev,
        [control]: learningMode
      }));
      toast.success(`MIDI: Control ${control} mapped to ${learningMode}`);
      setLearningMode(null);
      return;
    }

    // Execute mapped control
    const action = midiMapping[control];
    if (!action) return;

    const normalizedValue = value / 127; // 0-1
    const deck = focusedDeck;

    switch (action) {
      case 'volume':
        updateVolume(deck, [normalizedValue * 100]);
        break;
      case 'crossfader':
        updateCrossfader([normalizedValue * 100]);
        break;
      case 'filter':
        const effects = deck === 'left' ? leftEffects : rightEffects;
        const setEffects = deck === 'left' ? setLeftEffects : setRightEffects;
        setEffects({ ...effects, filter: normalizedValue * 100 });
        break;
      case 'echo':
        const effects2 = deck === 'left' ? leftEffects : rightEffects;
        const setEffects2 = deck === 'left' ? setLeftEffects : setRightEffects;
        setEffects2({ ...effects2, echo: normalizedValue * 100 });
        break;
      case 'pitch':
        updatePitch(deck, [(normalizedValue * 100) - 50]); // -50 to +50
        break;
      default:
        break;
    }
  };

  // Start MIDI learn mode
  const startMIDILearn = (action) => {
    setLearningMode(action);
    toast.info(`MIDI Learn: Move a control for "${action}"`);
  };

  // Spectrum Analyzer Component (Fixed - single audio context per element)
  const SpectrumAnalyzer = ({ audioElement, color, deckName }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const analyserRef = useRef(null);
    const audioContextRef = useRef(null);

    useEffect(() => {
      if (!audioElement || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = canvas.offsetWidth;
      canvas.height = 80;

      let setupDone = false;

      const setupAnalyser = () => {
        if (setupDone) return;
        setupDone = true;

        try {
          const AudioContext = window.AudioContext || window.webkitAudioContext;
          audioContextRef.current = new AudioContext();
          const analyser = audioContextRef.current.createAnalyser();
          analyser.fftSize = 256;

          const source = audioContextRef.current.createMediaElementSource(audioElement);
          source.connect(analyser);
          analyser.connect(audioContextRef.current.destination);
          analyserRef.current = analyser;

          console.log(`Spectrum analyzer connected for ${deckName}`);
        } catch (err) {
          console.warn(`Spectrum analyzer error for ${deckName}:`, err);
        }
      };

      const draw = () => {
        if (!analyserRef.current) {
          animationRef.current = requestAnimationFrame(draw);
          return;
        }

        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyserRef.current.getByteFrequencyData(dataArray);

        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const barWidth = (canvas.width / bufferLength) * 2.5;
        let x = 0;

        for (let i = 0; i < bufferLength; i++) {
          const barHeight = (dataArray[i] / 255) * canvas.height;
          
          const gradient = ctx.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
          gradient.addColorStop(0, color);
          gradient.addColorStop(1, color + '40');
          
          ctx.fillStyle = gradient;
          ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
          x += barWidth + 1;
        }

        animationRef.current = requestAnimationFrame(draw);
      };

      audioElement.addEventListener('play', setupAnalyser, { once: true });
      draw();

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        audioElement.removeEventListener('play', setupAnalyser);
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
        }
      };
    }, [audioElement, color, deckName]);

    return <canvas ref={canvasRef} className="w-full h-20 rounded" />;
  };

  // Helper to get musical key
  const CAMELOT_TO_KEY = {
    "1A": "G#m", "1B": "B", "2A": "D#m", "2B": "F#", "3A": "A#m", "3B": "C#",
    "4A": "Fm", "4B": "G#", "5A": "Cm", "5B": "D#", "6A": "Gm", "6B": "A#",
    "7A": "Dm", "7B": "F", "8A": "Am", "8B": "C", "9A": "Em", "9B": "G",
    "10A": "Bm", "10B": "D", "11A": "F#m", "11B": "A", "12A": "C#m", "12B": "E"
  };

  const getActualBPM = (originalBPM, pitchPercent) => {
    if (!originalBPM) return null;
    return Math.round(originalBPM * (1 + pitchPercent / 100) * 10) / 10;
  };

  if (performanceMode) {
    // PERFORMANCE MODE - Large waveforms & quick actions
    return (
      <div className="h-full w-full flex flex-col p-4 gap-3 overflow-hidden bg-black">
        {/* Performance Mode Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-cyan-400">PERFORMANCE MODE</h2>
          <Button onClick={() => setPerformanceMode(false)} className="bg-red-600 hover:bg-red-700">
            <Maximize2 className="w-4 h-4 mr-2" />
            Exit Performance
          </Button>
        </div>

        {/* Large Waveforms */}
        <div className="grid grid-cols-2 gap-4 flex-1">
          {/* Left Deck */}
          <Card className="bg-gray-900/90 border-cyan-500/30">
            <CardContent className="p-4 h-full flex flex-col">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-cyan-400 font-bold text-xl">LEFT DECK</h3>
                <Button onClick={() => togglePlay('left')} disabled={!leftDeck.track} className={`${leftDeck.playing ? 'bg-red-600' : 'bg-green-600'} w-24 h-12`}>
                  {leftDeck.playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </Button>
              </div>
              
              {leftDeck.track ? (
                <>
                  <div className="mb-3">
                    <p className="text-white text-lg font-bold">{leftDeck.track.title}</p>
                    <p className="text-gray-400">{leftDeck.track.artist}</p>
                  </div>
                  
                  <div className="flex-1 bg-black rounded-lg p-2 flex items-center">
                    {leftAudioRef.current && <WaveformV2 audioElement={leftAudioRef.current} color="#22d3ee" progressColor="#06b6d4" height={200} />}
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {[0,1,2,3].map(i => (
                      <Button key={i} onClick={() => leftDeck.cuePoints[i] && jumpToCue('left', leftDeck.cuePoints[i])} disabled={!leftDeck.cuePoints[i]} className={`${leftDeck.cuePoints[i] ? 'bg-cyan-600' : 'bg-gray-700'} h-12 text-sm`}>
                        CUE {i+1}
                      </Button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <Music2 className="w-16 h-16 mb-2" />
                  <p>No track loaded</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right Deck */}
          <Card className="bg-gray-900/90 border-pink-500/30">
            <CardContent className="p-4 h-full flex flex-col">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-pink-400 font-bold text-xl">RIGHT DECK</h3>
                <Button onClick={() => togglePlay('right')} disabled={!rightDeck.track} className={`${rightDeck.playing ? 'bg-red-600' : 'bg-green-600'} w-24 h-12`}>
                  {rightDeck.playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </Button>
              </div>
              
              {rightDeck.track ? (
                <>
                  <div className="mb-3">
                    <p className="text-white text-lg font-bold">{rightDeck.track.title}</p>
                    <p className="text-gray-400">{rightDeck.track.artist}</p>
                  </div>
                  
                  <div className="flex-1 bg-black rounded-lg p-2 flex items-center">
                    {rightAudioRef.current && <WaveformV2 audioElement={rightAudioRef.current} color="#f472b6" progressColor="#ec4899" height={200} />}
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {[0,1,2,3].map(i => (
                      <Button key={i} onClick={() => rightDeck.cuePoints[i] && jumpToCue('right', rightDeck.cuePoints[i])} disabled={!rightDeck.cuePoints[i]} className={`${rightDeck.cuePoints[i] ? 'bg-pink-600' : 'bg-gray-700'} h-12 text-sm`}>
                        CUE {i+1}
                      </Button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <Music2 className="w-16 h-16 mb-2" />
                  <p>No track loaded</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Crossfader */}
        <Card className="bg-gray-900/90 border-purple-500/30">
          <CardContent className="p-3">
            <div className="flex items-center gap-4">
              <span className="text-purple-400 font-bold">CROSSFADER</span>
              <div className="flex-1">
                <Slider value={[crossfader]} onValueChange={updateCrossfader} max={100} className="touch-manipulation" />
              </div>
              <Button onClick={syncBPM} className="bg-yellow-600 hover:bg-yellow-700 font-bold">
                SYNC
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col p-3 gap-2 overflow-hidden">
      {/* Top Controls Bar */}
      <div className="flex gap-2 items-center justify-between bg-gray-900/50 p-2 rounded-lg border border-cyan-500/20">
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowEffects(!showEffects)} className={`${showEffects ? 'bg-cyan-600' : 'bg-gray-700'} touch-manipulation text-xs h-8`}>
            <Sliders className="w-3 h-3 mr-1" />Effects
          </Button>
          <Button size="sm" onClick={() => setShowSpectrum(!showSpectrum)} className={`${showSpectrum ? 'bg-purple-600' : 'bg-gray-700'} touch-manipulation text-xs h-8`}>
            <Activity className="w-3 h-3 mr-1" />Spectrum
          </Button>
          <Button size="sm" onClick={() => setShowMIDI(!showMIDI)} className={`${showMIDI ? 'bg-pink-600' : 'bg-gray-700'} touch-manipulation text-xs h-8`}>
            <Radio className="w-3 h-3 mr-1" />MIDI
          </Button>
          <Button size="sm" onClick={() => setPerformanceMode(true)} className="bg-orange-600 hover:bg-orange-700 touch-manipulation text-xs h-8">
            <Maximize2 className="w-3 h-3 mr-1" />Performance
          </Button>
        </div>
        
        <div className="flex gap-2 items-center">
          <Badge className="bg-green-900/30 text-green-400 border-green-700/30 text-xs">
            Focused: {focusedDeck.toUpperCase()}
          </Badge>
          {midiDevices.length > 0 && (
            <Badge className="bg-pink-900/30 text-pink-400 border-pink-700/30 text-xs">
              MIDI: {midiDevices.length} device(s)
            </Badge>
          )}
        </div>
      </div>

      {/* Main Content - 3 Column Grid */}
      <div className="flex-1 grid grid-cols-3 gap-3 overflow-hidden min-h-0">
        {/* LEFT DECK */}
        <div className="flex flex-col gap-2 overflow-y-auto">
          <Card className="bg-gray-900/80 border-cyan-500/30">
            <CardContent className="p-2">
              <h3 className="text-cyan-400 font-bold mb-2 text-sm">LEFT DECK</h3>
              
              {leftDeck.track ? (
                <div className="mb-2">
                  <p className="text-white text-sm font-semibold truncate">{leftDeck.track.title}</p>
                  <p className="text-gray-400 text-xs truncate">{leftDeck.track.artist}</p>
                  <div className="flex gap-1 mt-1">
                    {leftDeck.track.bpm && <Badge className="bg-cyan-900/30 text-cyan-400 text-xs">{leftDeck.track.bpm}</Badge>}
                    {leftDeck.track.camelot_key && <Badge className="bg-pink-900/30 text-pink-400 text-xs">{CAMELOT_TO_KEY[leftDeck.track.camelot_key]}</Badge>}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-xs mb-2">No track loaded</p>
              )}

              {leftDeck.track && <WaveformV2 audioElement={leftAudioRef.current} color="#22d3ee" progressColor="#06b6d4" height={60} />}
              
              {showSpectrum && leftDeck.track && (
                <div className="mt-2">
                  <SpectrumAnalyzer audioElement={leftAudioRef.current} color="#22d3ee" deckName="Left" />
                </div>
              )}

              <div className="flex justify-center gap-2 my-2">
                <Button size="sm" onClick={() => togglePlay('left')} disabled={!leftDeck.track} className={`${leftDeck.playing ? 'bg-red-600' : 'bg-green-600'} text-white w-16 h-8`}>
                  {leftDeck.playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </Button>
              </div>

              <div className="mb-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-400">CUES</span>
                  <Button size="sm" onClick={() => setCuePoint('left')} className="bg-green-600 h-6 text-xs">+</Button>
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {[0,1,2,3].map(i => (
                    <Button key={i} size="sm" onClick={() => leftDeck.cuePoints[i] && jumpToCue('left', leftDeck.cuePoints[i])} disabled={!leftDeck.cuePoints[i]} className={`${leftDeck.cuePoints[i] ? 'bg-cyan-600' : 'bg-gray-700'} text-xs h-6`}>
                      {i+1}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="mb-2 bg-gray-800/30 rounded p-2">
                <span className="text-xs text-gray-400 block mb-1">LOOP</span>
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => setLoopPoint('left', 'start')} className="bg-blue-600 text-xs flex-1 h-6">In</Button>
                  <Button size="sm" onClick={() => setLoopPoint('left', 'end')} className="bg-purple-600 text-xs flex-1 h-6">Out</Button>
                  <Button size="sm" onClick={() => toggleLoop('left')} className={`${leftDeck.looping ? 'bg-green-600' : 'bg-gray-600'} text-xs flex-1 h-6`}>
                    {leftDeck.looping ? 'ON' : 'OFF'}
                  </Button>
                </div>
              </div>

              <div className="mb-2">
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-gray-400">PITCH</span>
                  <span className="text-xs text-white">{leftDeck.pitch > 0 ? '+' : ''}{leftDeck.pitch}%</span>
                  {leftDeck.track?.bpm && <Badge className="bg-green-900/30 text-green-400 text-xs">{getActualBPM(leftDeck.track.bpm, leftDeck.pitch)} BPM</Badge>}
                </div>
                <Slider value={[leftDeck.pitch]} onValueChange={(v) => updatePitch('left', v)} min={-50} max={50} step={1} className="touch-manipulation" />
              </div>

              <div className="mb-2 bg-gray-800/30 rounded p-2 space-y-1">
                <span className="text-xs text-gray-400 block">EQ</span>
                <div>
                  <div className="flex justify-between"><span className="text-xs text-gray-500">HIGH</span><span className="text-xs">{leftDeck.eqHigh}%</span></div>
                  <Slider value={[leftDeck.eqHigh]} onValueChange={(v) => updateEQ('left', 'High', v)} max={100} className="touch-manipulation" />
                </div>
                <div>
                  <div className="flex justify-between"><span className="text-xs text-gray-500">MID</span><span className="text-xs">{leftDeck.eqMid}%</span></div>
                  <Slider value={[leftDeck.eqMid]} onValueChange={(v) => updateEQ('left', 'Mid', v)} max={100} className="touch-manipulation" />
                </div>
                <div>
                  <div className="flex justify-between"><span className="text-xs text-gray-500">LOW</span><span className="text-xs">{leftDeck.eqLow}%</span></div>
                  <Slider value={[leftDeck.eqLow]} onValueChange={(v) => updateEQ('left', 'Low', v)} max={100} className="touch-manipulation" />
                </div>
              </div>

              {showEffects && (
                <div className="bg-cyan-900/20 rounded p-2 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-cyan-400 block">EFFECTS</span>
                    <Zap className="w-3 h-3 text-cyan-400" />
                  </div>
                  <div>
                    <div className="flex justify-between"><span className="text-xs text-gray-400">Echo</span><span className="text-xs">{leftEffects.echo}%</span></div>
                    <Slider value={[leftEffects.echo]} onValueChange={(v) => setLeftEffects({...leftEffects, echo: v[0]})} max={100} className="touch-manipulation" />
                  </div>
                  <div>
                    <div className="flex justify-between"><span className="text-xs text-gray-400">Reverb</span><span className="text-xs">{leftEffects.reverb}%</span></div>
                    <Slider value={[leftEffects.reverb]} onValueChange={(v) => setLeftEffects({...leftEffects, reverb: v[0]})} max={100} className="touch-manipulation" />
                  </div>
                  <div>
                    <div className="flex justify-between"><span className="text-xs text-gray-400">Filter</span><span className="text-xs">{leftEffects.filter}</span></div>
                    <Slider value={[leftEffects.filter]} onValueChange={(v) => setLeftEffects({...leftEffects, filter: v[0]})} max={100} className="touch-manipulation" />
                  </div>
                </div>
              )}

              <div className="mt-2">
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-cyan-400">VOLUME</span>
                  <span className="text-xs text-white">{leftDeck.volume}%</span>
                </div>
                <Slider value={[leftDeck.volume]} onValueChange={(v) => updateVolume('left', v)} max={100} className="touch-manipulation" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CENTER */}
        <div className="flex flex-col gap-2 overflow-y-auto">
          <div className="flex justify-center gap-2">
            <VUMeter audioElement={leftAudioRef.current} label="L" color="#22d3ee" height={100} />
            <VUMeter audioElement={rightAudioRef.current} label="R" color="#f472b6" height={100} />
          </div>

          <Card className="bg-gray-900/80 border-purple-500/30">
            <CardContent className="p-2">
              <div className="mb-2">
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-purple-400">MASTER</span>
                  <span className="text-xs text-white">{masterVolume}%</span>
                </div>
                <Slider value={[masterVolume]} onValueChange={updateMasterVolume} max={100} className="touch-manipulation" />
              </div>

              <div className="bg-gray-800/50 rounded p-2 mb-2">
                <div className="text-center mb-1">
                  <span className="text-xs font-bold text-white">CROSSFADER</span>
                  <div className="flex justify-between text-xs">
                    <span className={crossfader < 50 ? 'text-cyan-400' : 'text-gray-500'}>L</span>
                    <span className="text-purple-400">{crossfader}%</span>
                    <span className={crossfader > 50 ? 'text-pink-400' : 'text-gray-500'}>R</span>
                  </div>
                </div>
                <Slider value={[crossfader]} onValueChange={updateCrossfader} max={100} className="touch-manipulation" />
              </div>

              <Button onClick={syncBPM} className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 text-white font-bold text-xs h-8">
                SYNC BPM
              </Button>
            </CardContent>
          </Card>

          {showMIDI && (
            <Card className="bg-gray-900/80 border-purple-500/30">
              <CardContent className="p-2">
                <h3 className="text-purple-400 font-bold mb-1 text-xs flex items-center gap-2">
                  <Radio className="w-3 h-3" />
                  MIDI CONTROLLER
                </h3>
                {midiDevices.length === 0 ? (
                  <p className="text-gray-500 text-xs">No MIDI devices detected</p>
                ) : (
                  <div className="space-y-2">
                    <div className="space-y-1">
                      {midiDevices.map((device, idx) => (
                        <Button key={idx} size="sm" className={`w-full text-xs h-6 ${selectedMIDI === device.id ? 'bg-purple-600' : 'bg-gray-700'}`} onClick={() => {
                          setSelectedMIDI(device.id);
                          setupMIDIListener(device);
                        }}>
                          {device.name}
                        </Button>
                      ))}
                    </div>
                    
                    {learningMode ? (
                      <div className="bg-yellow-900/30 p-2 rounded border border-yellow-500/30">
                        <p className="text-yellow-400 text-xs mb-1">Learning: {learningMode}</p>
                        <Button size="sm" onClick={() => setLearningMode(null)} className="bg-red-600 w-full h-6 text-xs">Cancel</Button>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 mb-1">Map Controls:</p>
                        <Button size="sm" onClick={() => startMIDILearn('volume')} className="w-full bg-cyan-600 h-6 text-xs">Volume</Button>
                        <Button size="sm" onClick={() => startMIDILearn('filter')} className="w-full bg-pink-600 h-6 text-xs">Filter</Button>
                        <Button size="sm" onClick={() => startMIDILearn('crossfader')} className="w-full bg-purple-600 h-6 text-xs">Crossfader</Button>
                      </div>
                    )}
                    
                    {Object.keys(midiMapping).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <p className="text-xs text-gray-400 mb-1">Mapped:</p>
                        {Object.entries(midiMapping).map(([control, action]) => (
                          <div key={control} className="text-xs text-gray-300">
                            CC{control} → {action}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="flex-1 bg-gray-900/80 border-yellow-500/30 min-h-0">
            <CardContent className="p-2 h-full flex flex-col">
              <h3 className="text-yellow-400 font-bold mb-1 text-xs">TRACK LIBRARY</h3>
              <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
                {tracks.map((track) => (
                  <div key={track.id} onClick={() => setSelectedLibraryTrack(track)} onDoubleClick={() => loadTrackToDeck(track, focusedDeck)} className={`p-1 rounded text-xs cursor-pointer ${selectedLibraryTrack?.id === track.id ? 'bg-purple-600/20' : 'bg-gray-800/30'}`}>
                    <p className="font-semibold truncate">{track.title}</p>
                    <div className="flex gap-1">
                      {track.bpm && <Badge className="bg-cyan-900/30 text-cyan-400 text-xs">{track.bpm}</Badge>}
                    </div>
                  </div>
                ))}
              </div>
              {selectedLibraryTrack && (
                <div className="grid grid-cols-2 gap-1 mt-1">
                  <Button size="sm" onClick={() => loadTrackToDeck(selectedLibraryTrack, 'left')} className="bg-cyan-600 text-xs h-6">← L</Button>
                  <Button size="sm" onClick={() => loadTrackToDeck(selectedLibraryTrack, 'right')} className="bg-pink-600 text-xs h-6">R →</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT DECK */}
        <div className="flex flex-col gap-2 overflow-y-auto">
          <Card className="bg-gray-900/80 border-pink-500/30">
            <CardContent className="p-2">
              <h3 className="text-pink-400 font-bold mb-2 text-sm">RIGHT DECK</h3>
              
              {rightDeck.track ? (
                <div className="mb-2">
                  <p className="text-white text-sm font-semibold truncate">{rightDeck.track.title}</p>
                  <p className="text-gray-400 text-xs truncate">{rightDeck.track.artist}</p>
                  <div className="flex gap-1 mt-1">
                    {rightDeck.track.bpm && <Badge className="bg-pink-900/30 text-pink-400 text-xs">{rightDeck.track.bpm}</Badge>}
                    {rightDeck.track.camelot_key && <Badge className="bg-purple-900/30 text-purple-400 text-xs">{CAMELOT_TO_KEY[rightDeck.track.camelot_key]}</Badge>}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-xs mb-2">No track loaded</p>
              )}

              {rightDeck.track && <WaveformV2 audioElement={rightAudioRef.current} color="#f472b6" progressColor="#ec4899" height={60} />}
              
              {showSpectrum && rightDeck.track && (
                <div className="mt-2">
                  <SpectrumAnalyzer audioElement={rightAudioRef.current} color="#f472b6" deckName="Right" />
                </div>
              )}

              <div className="flex justify-center gap-2 my-2">
                <Button size="sm" onClick={() => togglePlay('right')} disabled={!rightDeck.track} className={`${rightDeck.playing ? 'bg-red-600' : 'bg-green-600'} text-white w-16 h-8`}>
                  {rightDeck.playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </Button>
              </div>

              <div className="mb-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-400">CUES</span>
                  <Button size="sm" onClick={() => setCuePoint('right')} className="bg-green-600 h-6 text-xs">+</Button>
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {[0,1,2,3].map(i => (
                    <Button key={i} size="sm" onClick={() => rightDeck.cuePoints[i] && jumpToCue('right', rightDeck.cuePoints[i])} disabled={!rightDeck.cuePoints[i]} className={`${rightDeck.cuePoints[i] ? 'bg-pink-600' : 'bg-gray-700'} text-xs h-6`}>
                      {i+1}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="mb-2 bg-gray-800/30 rounded p-2">
                <span className="text-xs text-gray-400 block mb-1">LOOP</span>
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => setLoopPoint('right', 'start')} className="bg-blue-600 text-xs flex-1 h-6">In</Button>
                  <Button size="sm" onClick={() => setLoopPoint('right', 'end')} className="bg-purple-600 text-xs flex-1 h-6">Out</Button>
                  <Button size="sm" onClick={() => toggleLoop('right')} className={`${rightDeck.looping ? 'bg-green-600' : 'bg-gray-600'} text-xs flex-1 h-6`}>
                    {rightDeck.looping ? 'ON' : 'OFF'}
                  </Button>
                </div>
              </div>

              <div className="mb-2">
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-gray-400">PITCH</span>
                  <span className="text-xs text-white">{rightDeck.pitch > 0 ? '+' : ''}{rightDeck.pitch}%</span>
                  {rightDeck.track?.bpm && <Badge className="bg-green-900/30 text-green-400 text-xs">{getActualBPM(rightDeck.track.bpm, rightDeck.pitch)} BPM</Badge>}
                </div>
                <Slider value={[rightDeck.pitch]} onValueChange={(v) => updatePitch('right', v)} min={-50} max={50} step={1} className="touch-manipulation" />
              </div>

              <div className="mb-2 bg-gray-800/30 rounded p-2 space-y-1">
                <span className="text-xs text-gray-400 block">EQ</span>
                <div>
                  <div className="flex justify-between"><span className="text-xs text-gray-500">HIGH</span><span className="text-xs">{rightDeck.eqHigh}%</span></div>
                  <Slider value={[rightDeck.eqHigh]} onValueChange={(v) => updateEQ('right', 'High', v)} max={100} className="touch-manipulation" />
                </div>
                <div>
                  <div className="flex justify-between"><span className="text-xs text-gray-500">MID</span><span className="text-xs">{rightDeck.eqMid}%</span></div>
                  <Slider value={[rightDeck.eqMid]} onValueChange={(v) => updateEQ('right', 'Mid', v)} max={100} className="touch-manipulation" />
                </div>
                <div>
                  <div className="flex justify-between"><span className="text-xs text-gray-500">LOW</span><span className="text-xs">{rightDeck.eqLow}%</span></div>
                  <Slider value={[rightDeck.eqLow]} onValueChange={(v) => updateEQ('right', 'Low', v)} max={100} className="touch-manipulation" />
                </div>
              </div>

              {showEffects && (
                <div className="bg-pink-900/20 rounded p-2 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-pink-400 block">EFFECTS</span>
                    <Zap className="w-3 h-3 text-pink-400" />
                  </div>
                  <div>
                    <div className="flex justify-between"><span className="text-xs text-gray-400">Echo</span><span className="text-xs">{rightEffects.echo}%</span></div>
                    <Slider value={[rightEffects.echo]} onValueChange={(v) => setRightEffects({...rightEffects, echo: v[0]})} max={100} className="touch-manipulation" />
                  </div>
                  <div>
                    <div className="flex justify-between"><span className="text-xs text-gray-400">Reverb</span><span className="text-xs">{rightEffects.reverb}%</span></div>
                    <Slider value={[rightEffects.reverb]} onValueChange={(v) => setRightEffects({...rightEffects, reverb: v[0]})} max={100} className="touch-manipulation" />
                  </div>
                  <div>
                    <div className="flex justify-between"><span className="text-xs text-gray-400">Filter</span><span className="text-xs">{rightEffects.filter}</span></div>
                    <Slider value={[rightEffects.filter]} onValueChange={(v) => setRightEffects({...rightEffects, filter: v[0]})} max={100} className="touch-manipulation" />
                  </div>
                </div>
              )}

              <div className="mt-2">
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-pink-400">VOLUME</span>
                  <span className="text-xs text-white">{rightDeck.volume}%</span>
                </div>
                <Slider value={[rightDeck.volume]} onValueChange={(v) => updateVolume('right', v)} max={100} className="touch-manipulation" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Keyboard Shortcuts Help */}
      <div className="bg-gray-900/50 p-1 rounded border border-cyan-500/20">
        <p className="text-xs text-gray-400 text-center">
          ⌨️ <span className="text-cyan-400">SPACE</span>: Play | <span className="text-cyan-400">1-4</span>: Cues | <span className="text-cyan-400">Q/W/E</span>: EQ Kill | <span className="text-cyan-400">↑/↓</span>: Bend | <span className="text-cyan-400">TAB</span>: Switch Deck
        </p>
      </div>
    </div>
  );
}
